import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyLocale } from '../utils/applyLocale';
import { postWebViewMessage } from '../utils/appNavigation';
import { SUPPORTED_LOCALES, SupportedLocale } from '../i18n';
import AppContext from '../contexts/AppContext';
import { ROLES } from '../utils/roleUtils';

const LOCALE_CONFIG: Record<SupportedLocale, { flag: string; label: string }> = {
  fr: { flag: '🇫🇷', label: 'French' },
  en: { flag: '🇬🇧', label: 'English' },
  es: { flag: '🇪🇸', label: 'Spanish' },
};

export interface LanguageSwitcherProps {
  /** The trigger sits on a dark bar (the app header). Defaults to true, which is where it shipped. */
  onDark?: boolean;
}

const LanguageSwitcher = ({ onDark = true }: LanguageSwitcherProps) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLocale = (SUPPORTED_LOCALES.includes(i18n.language as SupportedLocale)
    ? i18n.language
    : 'fr') as SupportedLocale;

  // Close on outside click.
  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Only superadmins may switch the interface language.
  if (currentUser?.role !== ROLES.SUPERADMIN) {
    return null;
  }

  const switchLanguage = (locale: SupportedLocale) => {
    applyLocale(locale);
    // Frontend-only: apply locally and sync the native shell. The chosen
    // language is never persisted to the account from the client.
    postWebViewMessage('SET_LOCALE', { locale });
    setOpen(false);
  };

  const { flag } = LOCALE_CONFIG[currentLocale];

  // Labels resolved with literal keys so the i18n static guard sees them.
  const labelFr = t('French');
  const labelEn = t('English');
  const labelEs = t('Spanish');
  const localizedLabels: Record<SupportedLocale, string> = { fr: labelFr, en: labelEn, es: labelEs };

  return (
    <div ref={containerRef} className="relative" aria-label={t('Language')}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-sm font-medium transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 ${
          onDark ? 'text-gray-300 focus:ring-white/60' : 'text-zinc-600 focus:ring-zinc-900 dark:text-zinc-300 dark:focus:ring-zinc-100'
        }`}
      >
        <span aria-hidden="true">{flag}</span>
        <span>{currentLocale.toUpperCase()}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('Language')}
          className="absolute right-0 top-full z-50 mt-1 min-w-[150px] overflow-hidden rounded-lg border border-zinc-200 bg-white py-1 shadow-lg dark:border-zinc-800 dark:bg-zinc-900"
        >
          {SUPPORTED_LOCALES.map((locale) => {
            const isActive = locale === currentLocale;
            const { flag: f } = LOCALE_CONFIG[locale];
            return (
              <li
                key={locale}
                role="option"
                aria-selected={isActive}
                onClick={() => switchLanguage(locale)}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-zinc-100 font-medium text-zinc-900 dark:bg-zinc-800 dark:text-zinc-50'
                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-50'
                }`}
              >
                <span aria-hidden="true">{f}</span>
                <span>{localizedLabels[locale]}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
